import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AlertService } from '@core/ui-kit/services/alert.service';
import { catchError, filter, groupBy, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { TopicSummaryStatus } from '../enums/topic-summary-status.enum';
import { TopicSummaryNotificationService } from '../service/topic-summary-notification.service';
import { TopicSummaryService } from '../service/topic-summary.service';
import { loadTopicSummaryActions } from './topic-summary.actions';

@Injectable()
export class TopicSummaryEffects {
  private readonly actions$ = inject(Actions);
  private readonly topicSummaryService = inject(TopicSummaryService);
  private readonly topicSummaryNotificationService = inject(TopicSummaryNotificationService);
  private readonly alertService = inject(AlertService);

  generateSummaryByID$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.generateTopicSummaryByID),
      switchMap(({ bundleId }) =>
        this.topicSummaryService.generateTopicSummary(bundleId).pipe(
          map(() =>
            loadTopicSummaryActions.loadTopicSummaryStructure({
              bundleId,
            }),
          ),
        ),
      ),
    ),
  );

  loadTopicSummaryStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.loadTopicSummaryStructure),
      mergeMap(({ bundleId }) =>
        this.topicSummaryService.getTopicSummaryStructure(bundleId).pipe(
          map((structure) => loadTopicSummaryActions.loadTopicSummaryStructureSuccess({ structure })),
          catchError((error) => {
            if (error.status === 400 && error.error.message === 'NOT_FOUND') {
              return of(loadTopicSummaryActions.loadTopicSummaryStructureSuccess({ structure: null }));
            }

            return of(
              loadTopicSummaryActions.loadTopicSummaryStructureFailure({
                summaryError: error.error.message,
              }),
            );
          }),
        ),
      ),
    ),
  );

  pollTopicSummaryStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.loadTopicSummaryStructureSuccess),
      filter(({ structure }) => structure?.status === TopicSummaryStatus.GENERATING),
      groupBy(({ structure }) => structure!.bundleId),
      mergeMap((group$) =>
        group$.pipe(
          take(1),
          switchMap(({ structure }) =>
            this.topicSummaryNotificationService.pollSummaryStructure(structure!.bundleId).pipe(
              map((finalStructure) =>
                loadTopicSummaryActions.loadTopicSummaryStructureSuccess({ structure: finalStructure }),
              ),
              catchError((error) => {
                if (error.status === 400 && error.error.message === 'NOT_FOUND') {
                  return of(loadTopicSummaryActions.loadTopicSummaryStructureSuccess({ structure: null }));
                }
                return of(
                  loadTopicSummaryActions.loadTopicSummaryStructureFailure({ summaryError: error.error.message }),
                );
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadSummaryById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.loadSummaryByID),
      switchMap(({ bundleId, topicId }) =>
        this.topicSummaryService.getTopicResults(bundleId, topicId).pipe(
          map((summary) =>
            loadTopicSummaryActions.loadSummaryByIDSuccess({
              summary,
            }),
          ),
          catchError((error) => {
            return of(loadTopicSummaryActions.loadSummaryByIDFailure({ summaryError: error.error.message }));
          }),
        ),
      ),
    ),
  );

  setParagraphPerformanceCriteria$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.setParagraphPerformanceCriteria),
      switchMap(({ bundleId, topicId, paragraphId, value }) =>
        this.topicSummaryService.setParagraphPerformanceCriteria(bundleId, topicId, paragraphId, value).pipe(
          map(() =>
            loadTopicSummaryActions.loadSummaryByID({
              bundleId,
              topicId,
            }),
          ),
        ),
      ),
    ),
  );

  deleteParagraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.deleteParagraph),
      switchMap(({ bundleId, topicId, resultsToDelete }) =>
        this.topicSummaryService.deleteTopicResults(bundleId, topicId, resultsToDelete).pipe(
          map(() =>
            loadTopicSummaryActions.loadSummaryByID({
              bundleId,
              topicId,
            }),
          ),
        ),
      ),
    ),
  );

  updateParagraphsOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.updateParagraphsOrder),
      switchMap(({ bundleId, topicId, payload }) =>
        this.topicSummaryService.updateParagraphsOrder(bundleId, topicId, payload).pipe(
          map(() =>
            loadTopicSummaryActions.loadSummaryByID({
              bundleId,
              topicId,
            }),
          ),
        ),
      ),
      tap(() => this.alertService.showOperationCompletedAlert()),
    ),
  );

  setParagraphScheldebouwCriteria$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.setParagraphScheldebouwCriteria),
      switchMap(({ bundleId, topicId, paragraphId, value }) =>
        this.topicSummaryService.setParagraphScheldebouwCriteria(bundleId, topicId, paragraphId, value).pipe(
          map(() =>
            loadTopicSummaryActions.loadSummaryByID({
              bundleId,
              topicId,
            }),
          ),
        ),
      ),
    ),
  );

  setParagraphComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.setParagraphComment),
      switchMap(({ bundleId, topicId, payload }) =>
        this.topicSummaryService.setParagraphComment(bundleId, topicId, payload).pipe(
          map(() =>
            loadTopicSummaryActions.loadSummaryByID({
              bundleId,
              topicId,
            }),
          ),
        ),
      ),
    ),
  );

  uploadSummaryStructureCSV$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.uploadSummaryStructureCSV),
      switchMap(({ bundleId, file }) =>
        this.topicSummaryService.updateSummaryTopicStructure(bundleId, file).pipe(
          switchMap(() => [
            loadTopicSummaryActions.clearSummary(),
            loadTopicSummaryActions.loadTopicSummaryStructure({
              bundleId,
            }),
          ]),
          tap(() => this.alertService.showOperationCompletedAlert()),
        ),
      ),
    ),
  );

  createNewParagraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTopicSummaryActions.createNewParagraph),
      switchMap(({ bundleId, topicId, payload }) =>
        this.topicSummaryService.createNewParagraph(bundleId, topicId, payload).pipe(
          map(() =>
            loadTopicSummaryActions.loadSummaryByID({
              bundleId,
              topicId,
            }),
          ),
          tap(() => this.alertService.showOperationCompletedAlert()),
        ),
      ),
    ),
  );
}
